import { CANVAS_SIZE } from "@/lib/constants/slides";
import type { SlideFields } from "@/types/Slide";
import type { LineElement, SlideElement } from "@ai-teach/types";
import { isFreeformTemplate, resolveSlideTemplate } from "@ai-teach/types";
import { getDefaultSlideData } from "@ai-teach/utils";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

/**
 * Editor state interface
 * Consolidated to use Slide type directly
 */
export interface SlideEditorState {
  // Consolidated slide data (matches backend Slide type)
  slide: SlideFields | null;

  // Editor-specific state (not part of slide)
  selectedId: string | null;
  canvasSize: {
    height: number;
    aspectRatio: number;
  };
  scale: number;
  rightClickMenu: {
    isOpen: boolean;
    position: { x: number; y: number };
    elementId: string | null;
  };
  clipboard: SlideElement | null;
  isAspectLocked: boolean;
  isLayerModalOpen: boolean;
  /** Which blank the fill-blanks type-mode UI is on — shared by the card and the bottom bar. */
  fillBlanksActiveBlank: number;
}

const initialState: SlideEditorState = {
  slide: null,
  selectedId: null,
  canvasSize: {
    height: 800,
    aspectRatio: 9 / 15,
  },
  scale: 1,
  rightClickMenu: {
    isOpen: false,
    position: { x: 0, y: 0 },
    elementId: null,
  },
  clipboard: null,
  isAspectLocked: false,
  isLayerModalOpen: false,
  fillBlanksActiveBlank: 0,
};

// Keeps zIndex non-negative so elements don't drop behind their own unpositioned Moveable controls
function normalizeZIndexes(elements: SlideElement[]) {
  [...elements]
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
    .forEach((el, index) => {
      el.zIndex = index;
    });
}

/**
 * Slide editor slice
 */
const slideEditorSlice = createSlice({
  name: "slideEditor",
  initialState,
  reducers: {
    // ========================================
    // SLIDE MANAGEMENT
    // ========================================

    // Load entire slide
    loadSlide: (state, action: PayloadAction<SlideFields>) => {
      const slide = action.payload;

      state.slide = slide;
      state.selectedId = null;

      const canvasSize = isFreeformTemplate(resolveSlideTemplate(slide))
        ? CANVAS_SIZE.freeform
        : CANVAS_SIZE.editor;

      state.canvasSize = {
        height: canvasSize.size.height,
        aspectRatio: canvasSize.aspectRatio,
      };
    },

    /**
     * Update fields on the open slide.
     *
     * `slideId` names the slide the changes were computed from and is required: work that
     * resolves after an await (AI generation, TTS, a file upload) carries data belonging to
     * whichever slide was open when it started, and the author may have moved on since. The
     * reducer drops those writes rather than merging one slide's content into another.
     */
    updateSlide: (state, action: PayloadAction<Partial<SlideFields> & { slideId: string }>) => {
      const { slideId, ...changes } = action.payload;
      if (!state.slide || state.slide.id !== slideId) return;
      state.slide = { ...state.slide, ...changes } as SlideFields;
    },

    // Set only slideData.subtitles — an async generation result must not clobber other edits
    setResponseSubtitles: (
      state,
      action: PayloadAction<{ slideId: string; subtitles: unknown[] }>
    ) => {
      const { slideId, subtitles } = action.payload;
      if (!state.slide || state.slide.id !== slideId) return;
      const sd = state.slide.slideData as { subtitles?: unknown[] } | null;
      if (sd) sd.subtitles = subtitles;
    },

    // Clear slide data
    clearSlide: (state) => {
      if (!state.slide) return;
      state.slide.elements = [];
      state.slide.slideData = getDefaultSlideData(state.slide.template);
      state.selectedId = null;
    },

    // Update slide name
    updateSlideName: (state, action: PayloadAction<string>) => {
      if (state.slide) {
        state.slide.name = action.payload;
      }
    },

    // ========================================
    // CANVAS MANAGEMENT
    // ========================================

    loadCanvasSize: (state, action: PayloadAction<{ height: number; aspectRatio: number }>) => {
      state.canvasSize = action.payload;
    },

    // ========================================
    // ELEMENT MANAGEMENT
    // ========================================

    // Add new element
    addElement: (state, action: PayloadAction<Omit<SlideElement, "id" | "zIndex">>) => {
      if (!state.slide) return;

      const newElement = {
        ...action.payload,
        zIndex: 0,
        id: uuidv4(),
      } as SlideElement;

      state.slide.elements.push(newElement);
      state.selectedId = newElement.id;
    },

    // Load element from My Styles
    loadElementFromMyStyles: (state, action: PayloadAction<SlideElement[]>) => {
      const elements = action.payload;
      const updatedElements = elements.map((el) => {
        return { ...el, id: uuidv4() };
      });

      if (state.slide) {
        state.slide.elements = updatedElements;
      }
    },

    // Update element position
    updateElementPosition: (state, action: PayloadAction<{ id: string; x: number; y: number }>) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload.id);
      if (element) {
        element.position = { x: action.payload.x, y: action.payload.y };
      }
    },

    // Update element size
    updateElementSize: (
      state,
      action: PayloadAction<{ id: string; width: number; height: number }>
    ) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload.id);
      if (element) {
        element.size = {
          width: action.payload.width,
          height: action.payload.height,
        };
      }
    },

    // Update element (position, size, and/or rotation)
    updateElement: (
      state,
      action: PayloadAction<{
        id: string;
        position?: { x: number; y: number };
        size?: { width: number; height: number };
        rotation?: number;
      }>
    ) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload.id);
      if (element) {
        if (action.payload.position) {
          element.position = action.payload.position;
        }
        if (action.payload.size) {
          element.size = action.payload.size;
        }
        if (action.payload.rotation !== undefined) {
          element.rotation = action.payload.rotation;
        }
      }
    },

    // Update line position
    updateLinePosition: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<LineElement> }>
    ) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((e) => e.id === action.payload.id);
      if (!element) return;

      // Narrow type so TS knows it's a line
      if (element.type !== "line") return;

      const { startX, startY, endX, endY } = action.payload.updates;

      if (startX !== undefined) element.startX = startX;
      if (startY !== undefined) element.startY = startY;
      if (endX !== undefined) element.endX = endX;
      if (endY !== undefined) element.endY = endY;
    },

    // Update element properties (for specific element types)
    updateElementProps: (
      state,
      action: PayloadAction<{ id: string; props: Partial<SlideElement> }>
    ) => {
      if (!state.slide) return;

      const index = state.slide.elements.findIndex((el) => el.id === action.payload.id);

      if (index !== -1) {
        state.slide.elements[index] = {
          ...state.slide.elements[index],
          ...action.payload.props,
        } as SlideElement;
      }
    },

    // Center element on the canvas' horizontal axis
    centerElementHorizontally: (state, action: PayloadAction<string>) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload);
      if (!element) return;

      const canvasWidth = state.canvasSize.height * state.canvasSize.aspectRatio;

      // Lines are drawn from their canvas-absolute endpoints, so shift those instead
      if (element.type === "line") {
        const left = Math.min(element.startX, element.endX);
        const right = Math.max(element.startX, element.endX);
        const delta = (canvasWidth - (right - left)) / 2 - left;

        element.startX += delta;
        element.endX += delta;
        return;
      }

      element.position.x = (canvasWidth - element.size.width) / 2;
    },

    // Delete element
    deleteElement: (state, action: PayloadAction<string>) => {
      if (!state.slide) return;

      state.slide.elements = state.slide.elements.filter((el) => el.id !== action.payload);
      if (state.selectedId === action.payload) {
        state.selectedId = null;
      }
    },

    // Update z-index
    bringToFront: (state, action: PayloadAction<string>) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload);
      if (element) {
        const maxZIndex = Math.max(...state.slide.elements.map((el) => el.zIndex || 0), 0);
        element.zIndex = maxZIndex + 1;
        normalizeZIndexes(state.slide.elements);
      }
    },

    sendToBack: (state, action: PayloadAction<string>) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload);
      if (element) {
        const minZIndex = Math.min(...state.slide.elements.map((el) => el.zIndex || 0), 0);
        element.zIndex = minZIndex - 1;
        normalizeZIndexes(state.slide.elements);
      }
    },

    bringForward: (state, action: PayloadAction<string>) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload);
      if (element) {
        element.zIndex = (element.zIndex || 0) + 1;
        normalizeZIndexes(state.slide.elements);
      }
    },

    sendBackward: (state, action: PayloadAction<string>) => {
      if (!state.slide) return;

      const element = state.slide.elements.find((el) => el.id === action.payload);
      if (element) {
        element.zIndex = (element.zIndex || 0) - 1;
        normalizeZIndexes(state.slide.elements);
      }
    },

    // ========================================
    // EDITOR-ONLY ACTIONS
    // ========================================

    // Select element
    setSelectedId: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },

    // Right-click menu actions
    openRightClickMenu: (
      state,
      action: PayloadAction<{
        position: { x: number; y: number };
        elementId: string;
      }>
    ) => {
      state.rightClickMenu.isOpen = true;
      state.rightClickMenu.position = action.payload.position;
      state.rightClickMenu.elementId = action.payload.elementId;
    },

    closeRightClickMenu: (state) => {
      state.rightClickMenu.isOpen = false;
      state.rightClickMenu.position = { x: 0, y: 0 };
      state.rightClickMenu.elementId = null;
    },

    // Clipboard actions
    copyElement: (state, action: PayloadAction<SlideElement>) => {
      state.clipboard = action.payload;
    },

    pasteElement: (state) => {
      if (!state.slide || !state.clipboard) return;

      function clamp(value: number, min: number, max: number) {
        return Math.min(Math.max(value, min), max);
      }

      const offsetX = 20;
      const offsetY = 30;

      const newPosition = {
        x: clamp(state.clipboard.position.x - offsetX, 0, 100),
        y: clamp(state.clipboard.position.y - offsetY, 0, 100),
      };

      const newElement = {
        ...state.clipboard,
        id: uuidv4(),
        position: newPosition,
      } as SlideElement;

      state.slide.elements.push(newElement);
      state.selectedId = newElement.id;
    },

    // Other editor actions
    toggleAspectLock: (state) => {
      state.isAspectLocked = !state.isAspectLocked;
    },

    setLayerModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isLayerModalOpen = action.payload;
    },

    setFillBlanksActiveBlank: (state, action: PayloadAction<number>) => {
      state.fillBlanksActiveBlank = action.payload;
    },
  },
});

export const {
  // Slide management
  loadSlide,
  updateSlide,
  setResponseSubtitles,
  clearSlide,
  updateSlideName,

  // Canvas management
  loadCanvasSize,

  // Element management
  addElement,
  loadElementFromMyStyles,
  updateElementPosition,
  updateElementSize,
  updateElement,
  updateLinePosition,
  updateElementProps,
  centerElementHorizontally,
  deleteElement,
  bringToFront,
  sendToBack,
  sendBackward,
  bringForward,
  // Editor-only actions
  setSelectedId,
  openRightClickMenu,
  closeRightClickMenu,
  copyElement,
  pasteElement,
  toggleAspectLock,
  setLayerModalOpen,
  setFillBlanksActiveBlank,
} = slideEditorSlice.actions;

export default slideEditorSlice.reducer;
